// src/api/payment.ts — Square payment lifecycle functions

import { Order, Payment, PaymentLink } from "square";
import { OrderItem } from "../types/OrderItem";
import { ApiResult } from "../types/ApiResult";
import { ShippingRateItem } from "../types/ShippingRate";
import { TaxRate } from "../types/TaxRate";
import { getPrintfulVariant } from "./printful";

// SquareKeys type is used by components that need to initialise the Square SDK
export type SquareKeys = { applicationId: string; locationId: string };

/** Fetch the Square application and location keys from the serverless function. */
export const getSquareKeys = async (): Promise<ApiResult<SquareKeys>> => {
  try {
    const response = await fetch(`/.netlify/functions/get-square-keys`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return response.json();
  } catch (error) {
    console.error("Error getting Square keys:", error);
    return { error: `Error getting Square keys: ${error}` };
  }
};

/** Fetch a single payment by ID.
 *  Access requires proof of ownership: either a customer JWT or the buyer email. */
export const getPayment = async (
  paymentId: string,
  opts?: { email?: string; authToken?: string },
): Promise<ApiResult<Payment>> => {
  if (!paymentId) return null;
  try {
    const params = new URLSearchParams({ id: paymentId });
    if (opts?.email) params.set("email", opts.email);
    const response = await fetch(
      `/.netlify/functions/get-payment?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(process.env.REACT_APP_FUNCTION_SECRET
            ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
            : {}),
          ...(opts?.authToken
            ? { Authorization: `Bearer ${opts.authToken}` }
            : {}),
        },
      },
    );
    return response.json();
  } catch (error) {
    console.error("Error getting payment:", error);
    return { error: `Error getting payment: ${error}` };
  }
};

/**
 * Create a new Square payment.
 * @param amount - The total amount in the smallest currency unit (cents).
 * @param opts.savedCardId - Charge a card on file instead of a fresh nonce. Pass
 *   `""` as `nonce` in that case. Requires `opts.authToken`: the server rejects a
 *   saved-card payment that doesn't prove the card belongs to the signed-in
 *   customer (see netlify/functions/create-payment.js).
 */
export const createPayment = async (
  nonce: string,
  amount: number,
  orderId: string,
  customerId: string,
  contactInfo: Payment,
  verificationToken?: string,
  idempotencyKey?: string,
  opts?: { savedCardId?: string; authToken?: string },
): Promise<ApiResult<Payment>> => {
  if ((!nonce && !opts?.savedCardId) || amount <= 0 || !orderId) return null;
  try {
    const response = await fetch("/.netlify/functions/create-payment", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(opts?.authToken
          ? { Authorization: `Bearer ${opts.authToken}` }
          : {}),
      },
      body: JSON.stringify({
        nonce,
        amount,
        orderId,
        customerId,
        contactInfo,
        verificationToken,
        idempotencyKey,
        ...(opts?.savedCardId ? { savedCardId: opts.savedCardId } : {}),
      }),
    });
    return response.json();
  } catch (error) {
    console.error("Error creating payment:", error);
    return { error: `Error creating payment: ${error}` };
  }
};

/** Capture an AUTHORIZED payment (move it to COMPLETED). */
export const capturePayment = async (
  paymentId: string,
): Promise<ApiResult<Payment>> => {
  if (!paymentId) return null;
  try {
    const response = await fetch("/.netlify/functions/capture-payment", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
      },
      body: JSON.stringify({ paymentId }),
    });
    return response.json();
  } catch (error) {
    console.error("Error capturing payment:", error);
    return { error: `Error capturing payment: ${error}` };
  }
};

/** Cancel an AUTHORIZED payment without charging the customer.
 *  Pass the auth token when logged in so the server can verify order ownership. */
export const cancelPayment = async (
  paymentId: string,
  authToken?: string,
): Promise<ApiResult<Payment>> => {
  if (!paymentId) return null;
  try {
    const response = await fetch("/.netlify/functions/cancel-payment", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({ paymentId }),
    });
    return response.json();
  } catch (error) {
    console.error("Error cancelling payment:", error);
    return { error: `Error cancelling payment: ${error}` };
  }
};

/**
 * Issue a full or partial refund for a COMPLETED payment.
 * @param amount - The amount to refund (bigint cents). Converted to Number before serialization.
 * @param authToken - Pass when logged in so the server can verify order ownership.
 */
export const refundPayment = async (
  paymentId: string,
  amount: bigint,
  reason?: string,
  authToken?: string,
): Promise<ApiResult<Payment>> => {
  if (!paymentId || !amount) return null;
  try {
    const response = await fetch("/.netlify/functions/refund-payment", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      // Convert bigint to Number before serialization — JSON.stringify silently drops bigint values
      body: JSON.stringify({ paymentId, amount: Number(amount), reason }),
    });
    return response.json();
  } catch (error) {
    console.error("Error refunding payment:", error);
    return { error: `Error refunding payment: ${error}` };
  }
};

/**
 * Calculate the available shipping rates for the given cart and contact info.
 * Returns every method Printful offers for the destination — typically STANDARD
 * plus PRINTFUL_FAST (Express). Express is only returned when `phone` is
 * supplied, so pass it whenever the customer has entered one.
 */
export const calculateShipping = async (
  orderItems: OrderItem[],
  contactInfo: Payment,
  phone?: string | null,
): Promise<ApiResult<ShippingRateItem[]>> => {
  if (!contactInfo || !orderItems || orderItems.length <= 0) return null;

  // SKUs store the Printful sync variant ID; the /shipping/rates endpoint needs
  // the base catalog variant_id. Resolve them in parallel before building line items.
  const syncVariantIds = orderItems.map(
    (item) => item.variation.itemVariationData?.sku?.split("_")?.[1],
  );
  const variantResults = await Promise.all(
    syncVariantIds.map((id) =>
      id ? getPrintfulVariant(id) : Promise.resolve(null),
    ),
  );

  const lineItems = orderItems.map((item, i) => ({
    name: item.product.itemData?.name ?? "",
    quantity: item.quantity,
    price: item.variation.itemVariationData?.priceMoney?.amount,
    variantId:
      (variantResults[i] as any)?.data?.variant_id ?? syncVariantIds[i],
  }));

  try {
    const response = await fetch("/.netlify/functions/calculate-shipping", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
      },
      body: JSON.stringify({ lineItems, contactInfo, phone }),
    });
    return response.json();
  } catch (error) {
    console.error("Error calculating shipping:", error);
    return { error: `Error calculating shipping: ${error}` };
  }
};

/** Calculate the applicable sales tax rate for the given contact/address and order value. */
export const calculateTax = async (
  contactInfo: Payment,
  total: number,
  shippingRate: string,
): Promise<ApiResult<TaxRate>> => {
  if (!contactInfo || !total || total === 0 || !shippingRate) return null;
  try {
    const response = await fetch("/.netlify/functions/calculate-tax", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
      },
      body: JSON.stringify({ contactInfo, total, shippingRate }),
    });
    return response.json();
  } catch (error) {
    console.error("Error calculating tax:", error);
    return { error: `Error calculating tax: ${error}` };
  }
};

/** Calculate the order total (including tax and donation) without creating it. */
export const calculateOrder = async (
  orderItems: OrderItem[],
  contactInfo: Payment,
  donationAmount: number,
  taxPercentage?: number,
  taxAmount?: number,
): Promise<ApiResult<Order>> => {
  if (!orderItems || orderItems.length <= 0) return null;

  const lineItems = orderItems.map((item) => ({
    name: item.product.itemData?.name ?? "",
    quantity: item.quantity,
    price: item.variation.itemVariationData?.priceMoney?.amount,
    variationId: item.variation.id,
  }));

  try {
    const response = await fetch("/.netlify/functions/calculate-order", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
      },
      body: JSON.stringify({
        lineItems,
        contactInfo,
        donationAmount,
        taxPercentage,
        taxAmount,
      }),
    });
    return response.json();
  } catch (error) {
    console.error("Error calculating order:", error);
    return { error: `Error calculating order: ${error}` };
  }
};

/** Create a Square payment link (used for the non-custom checkout flow). */
export const createPaymentLink = async (
  orderItems: OrderItem[],
): Promise<ApiResult<PaymentLink>> => {
  if (!orderItems || orderItems.length <= 0) return null;

  const lineItems = orderItems.map((item) => ({
    name: item.product.itemData?.name ?? "",
    quantity: item.quantity,
    price: item.variation.itemVariationData?.priceMoney?.amount,
    variationId: item.variation.id,
  }));

  try {
    const response = await fetch("/.netlify/functions/create-payment-link", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
      },
      body: JSON.stringify({ lineItems }),
    });
    return response.json();
  } catch (error) {
    console.error("Error creating payment link:", error);
    return { error: `Error creating payment link: ${error}` };
  }
};
